/**
 * Reducer for ImageModelSelector component state.
 *
 * Consolidates 12+ useState declarations into a single predictable state atom.
 */

import type { ImageModelInfo } from '@/types';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface ImageModelSelectorState {
  // Local model list (from server)
  models: ImageModelInfo[];
  defaultModel: string | null;
  supportsModels: boolean;
  isLoading: boolean;
  error: string | null;

  // Search / CivitAI
  searchQuery: string;
  civitaiModels: ImageModelInfo[];
  popularModels: ImageModelInfo[];
  isSearching: boolean;
  searchError: string | null;
  popularLoaded: boolean;

  // Image fallback tracking
  brokenImages: Set<string>;
}

export const initialState: ImageModelSelectorState = {
  models: [],
  defaultModel: null,
  supportsModels: false,
  isLoading: false,
  error: null,

  searchQuery: '',
  civitaiModels: [],
  popularModels: [],
  isSearching: false,
  searchError: null,
  popularLoaded: false,

  brokenImages: new Set<string>(),
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type ImageModelSelectorAction =
  // Model loading
  | { type: 'MODELS_LOADING' }
  | {
      type: 'MODELS_LOADED';
      models: ImageModelInfo[];
      defaultModel: string | null;
      supportsModels: boolean;
    }
  | { type: 'MODELS_ERROR'; error: string }

  // Search
  | { type: 'SEARCH_QUERY_CHANGED'; query: string }
  | { type: 'SEARCH_STARTED' }
  | { type: 'SEARCH_RESULTS'; models: ImageModelInfo[] }
  | { type: 'SEARCH_ERROR'; error: string }
  | { type: 'SEARCH_CLEARED' }

  // Popular models
  | { type: 'POPULAR_LOADED'; models: ImageModelInfo[] }

  // Image error fallback
  | { type: 'IMAGE_BROKEN'; key: string };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function imageModelSelectorReducer(
  state: ImageModelSelectorState,
  action: ImageModelSelectorAction
): ImageModelSelectorState {
  switch (action.type) {
    case 'MODELS_LOADING':
      return { ...state, isLoading: true, error: null };

    case 'MODELS_LOADED':
      return {
        ...state,
        isLoading: false,
        models: action.models,
        defaultModel: action.defaultModel,
        supportsModels: action.supportsModels,
        error: null,
      };

    case 'MODELS_ERROR':
      return {
        ...state,
        isLoading: false,
        models: [],
        supportsModels: false,
        error: action.error,
      };

    case 'SEARCH_QUERY_CHANGED':
      return { ...state, searchQuery: action.query, searchError: null };

    case 'SEARCH_STARTED':
      return { ...state, isSearching: true };

    case 'SEARCH_RESULTS':
      return { ...state, isSearching: false, civitaiModels: action.models, searchError: null };

    case 'SEARCH_ERROR':
      return { ...state, isSearching: false, civitaiModels: [], searchError: action.error };

    case 'SEARCH_CLEARED':
      return { ...state, isSearching: false, civitaiModels: [], searchError: null };

    case 'POPULAR_LOADED':
      return { ...state, popularLoaded: true, popularModels: action.models };

    case 'IMAGE_BROKEN': {
      const next = new Set(state.brokenImages);
      next.add(action.key);
      return { ...state, brokenImages: next };
    }

    default:
      return state;
  }
}
